import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

// Generate hours (1-12)
const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

// Generate minutes in 5-minute increments
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

const PERIODS = ['AM', 'PM'];

// Duration options in minutes
const DURATIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '1 hour' },
  { value: '90', label: '1.5 hours' },
  { value: '120', label: '2 hours' },
  { value: '180', label: '3 hours' },
  { value: '240', label: '4 hours' },
];

// Format date to local YYYY-MM-DDTHH:mm string (avoids UTC shift from toISOString)
function toLocalDateTimeStr(date) {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

function parseDateTime(dateTimeStr) {
  if (!dateTimeStr) {
    const now = new Date();
    return {
      date: now,
      hour: '09',
      minute: '00',
      period: 'AM'
    };
  }
  
  const date = new Date(dateTimeStr);
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  if (hours === 0) hours = 12;
  else if (hours > 12) hours = hours - 12;
  
  // Round minutes to nearest 5
  const roundedMinutes = Math.round(minutes / 5) * 5;
  
  return {
    date: date,
    hour: hours.toString().padStart(2, '0'),
    minute: (roundedMinutes % 60).toString().padStart(2, '0'),
    period: period
  };
}

function buildDateTime(date, hour, minute, period) {
  const d = new Date(date);
  let h = parseInt(hour);
  
  // Convert from 12-hour to 24-hour
  if (period === 'AM' && h === 12) h = 0;
  else if (period === 'PM' && h !== 12) h = h + 12;
  
  d.setHours(h, parseInt(minute), 0, 0);
  return d;
}

function formatDisplayDate(date) {
  if (!date) return "Select date";
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// Standard DateTimePicker for Tasks
export const DateTimePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select date and time",
  className,
  disabled = false
}) => {
  const parsed = parseDateTime(value);
  const [selectedDate, setSelectedDate] = useState(parsed.date);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const parsed = parseDateTime(value);
    setSelectedDate(parsed.date);
    setHour(parsed.hour);
    setMinute(parsed.minute);
    setPeriod(parsed.period);
  }, [value]);

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      const newDateTime = buildDateTime(date, hour, minute, period);
      onChange(toLocalDateTimeStr(newDateTime));
      setCalendarOpen(false);
    }
  };

  const handleTimeChange = (newHour, newMinute, newPeriod) => {
    const h = newHour !== null ? newHour : hour;
    const m = newMinute !== null ? newMinute : minute;
    const p = newPeriod !== null ? newPeriod : period;
    
    if (newHour !== null) setHour(newHour);
    if (newMinute !== null) setMinute(newMinute);
    if (newPeriod !== null) setPeriod(newPeriod);
    
    const newDateTime = buildDateTime(selectedDate, h, m, p);
    onChange(toLocalDateTimeStr(newDateTime));
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Date Picker */}
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal h-10 rounded-sm border-slate-200 text-sm",
              !selectedDate && "text-slate-500"
            )}
          >
            <CalendarDays className="mr-2 h-4 w-4 text-slate-400" />
            {formatDisplayDate(selectedDate)}
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-0 rounded-sm border-slate-200 shadow-md" 
          align="start"
          sideOffset={4}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {/* Time Picker */}
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-slate-400 flex-shrink-0" />
        
        {/* Hour */}
        <Select value={hour} onValueChange={(v) => handleTimeChange(v, null, null)} disabled={disabled}>
          <SelectTrigger className="w-[70px] h-10 rounded-sm border-slate-200">
            <SelectValue placeholder="HH" />
          </SelectTrigger>
          <SelectContent className="rounded-sm border-slate-200">
            {HOURS.map(h => (
              <SelectItem key={h} value={h}>{h}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-slate-400 font-medium">:</span>

        {/* Minute */}
        <Select value={minute} onValueChange={(v) => handleTimeChange(null, v, null)} disabled={disabled}>
          <SelectTrigger className="w-[70px] h-10 rounded-sm border-slate-200">
            <SelectValue placeholder="MM" />
          </SelectTrigger>
          <SelectContent className="rounded-sm border-slate-200">
            {MINUTES.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* AM/PM */}
        <Select value={period} onValueChange={(v) => handleTimeChange(null, null, v)} disabled={disabled}>
          <SelectTrigger className="w-[70px] h-10 rounded-sm border-slate-200">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-sm border-slate-200">
            {PERIODS.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// Event DateTime Picker with Duration (for Events only)
export const EventDateTimePicker = ({
  startValue,
  duration = '60',
  onStartChange,
  onDurationChange,
  occupiedHours = [],
  disabled = false
}) => {
  const parsed = parseDateTime(startValue);
  const [selectedDate, setSelectedDate] = useState(parsed.date);
  const [hour, setHour] = useState(parsed.hour);
  const [minute, setMinute] = useState(parsed.minute);
  const [period, setPeriod] = useState(parsed.period);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Track last value we emitted to avoid the useEffect re-parsing our own update
  const lastEmitted = useRef(null);

  useEffect(() => {
    // Skip if this update was triggered by our own handleTimeChange/handleDateSelect
    if (lastEmitted.current && lastEmitted.current === startValue) {
      lastEmitted.current = null;
      return;
    }
    const parsed = parseDateTime(startValue);
    setSelectedDate(parsed.date);
    setHour(parsed.hour);
    setMinute(parsed.minute);
    setPeriod(parsed.period);
  }, [startValue]);

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      const newDateTime = buildDateTime(date, hour, minute, period);
      const str = toLocalDateTimeStr(newDateTime);
      lastEmitted.current = str;
      onStartChange(str);
      setCalendarOpen(false);
    }
  };

  const handleTimeChange = (newHour, newMinute, newPeriod) => {
    const h = newHour !== null ? newHour : hour;
    const m = newMinute !== null ? newMinute : minute;
    const p = newPeriod !== null ? newPeriod : period;

    if (newHour !== null) setHour(newHour);
    if (newMinute !== null) setMinute(newMinute);
    if (newPeriod !== null) setPeriod(newPeriod);

    const newDateTime = buildDateTime(selectedDate, h, m, p);
    const str = toLocalDateTimeStr(newDateTime);
    lastEmitted.current = str;
    onStartChange(str);
  };

  return (
    <div className="space-y-4">
      {/* Date Picker */}
      <div>
        <label className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-1.5 block">Date</label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start text-left font-normal h-10 rounded-sm border-slate-200 text-sm",
                !selectedDate && "text-slate-500"
              )}
            >
              <CalendarDays className="mr-2 h-4 w-4 text-slate-400" />
              {formatDisplayDate(selectedDate)}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-0 rounded-sm border-slate-200 shadow-md" 
            align="start"
            sideOffset={4}
          >
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Time and Duration Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Start Time */}
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-1.5 block">Start Time</label>
          <div className="flex items-center gap-1.5">
            <Select value={hour} onValueChange={(v) => handleTimeChange(v, null, null)} disabled={disabled}>
              <SelectTrigger className="w-[60px] h-10 rounded-sm border-slate-200">
                <SelectValue placeholder="HH" />
              </SelectTrigger>
              <SelectContent className="rounded-sm border-slate-200">
                {HOURS.map(h => (
                  <SelectItem key={h} value={h} disabled={occupiedHours.includes(h + ':' + minute + ' ' + period)}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <span className="text-slate-400">:</span>

            <Select value={minute} onValueChange={(v) => handleTimeChange(null, v, null)} disabled={disabled}>
              <SelectTrigger className="w-[60px] h-10 rounded-sm border-slate-200">
                <SelectValue placeholder="MM" />
              </SelectTrigger>
              <SelectContent className="rounded-sm border-slate-200">
                {MINUTES.map(m => (
                  <SelectItem key={m} value={m} disabled={occupiedHours.includes(hour + ':' + m + ' ' + period)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={period} onValueChange={(v) => handleTimeChange(null, null, v)} disabled={disabled}>
              <SelectTrigger className="w-[76px] h-10 rounded-sm border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm border-slate-200">
                {PERIODS.map(p => (
                  <SelectItem key={p} value={p} disabled={occupiedHours.includes(hour + ':' + minute + ' ' + p)}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Duration */}
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-1.5 block">Duration</label>
          <Select value={duration} onValueChange={onDurationChange} disabled={disabled}>
            <SelectTrigger className="w-full h-10 rounded-sm border-slate-200">
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent className="rounded-sm border-slate-200">
              {DURATIONS.map(d => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default DateTimePicker;
